import {put, call, all} from 'redux-saga/effects';
import ActivityCategory from "../../entities/activityCategory";
import * as Actions from "../actions/activityCategoryActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ActivityCategoryAction = {
    activityCategoryId: string;
    activityCategory: ActivityCategory;
    activityCategories: ActivityCategory[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneActivityCategory(action: ActivityCategoryAction) {
	const session = tabSession.captureGeneration();
	try {
		const activityCategory: ActivityCategory = yield call(api.findOne, 'activityCategory', action.activityCategoryId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ACTIVITYCATEGORY_FETCH_ONE_SUCCESS, activityCategoryId: action.activityCategoryId, activityCategory, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ACTIVITYCATEGORY_FETCH_ONE_FAILURE, activityCategoryId: action.activityCategoryId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ActivityCategoryAction} action
 */
export function* saveActivityCategory(action: ActivityCategoryAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ ACTIVITYCATEGORY_SAVE saga', action.activityCategory?.id);

		if (action.activityCategory === undefined || action.activityCategory === null) {
			console.error('saveActivityCategory received a null');
			throw new Error('saveActivityCategory received a null');
		}

		if (!(action.activityCategory instanceof ActivityCategory)) {
			action.activityCategory = new ActivityCategory(action.activityCategory);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.activityCategory.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.activityCategory);
			const addResult = yield call(idb.add, 'activityCategory', action.activityCategory);
			if (addResult.isErr()) {
				console.error(`⮂ saveActivityCategory: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertActivityCategorySave(action.activityCategory, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.ACTIVITYCATEGORY_SAVE_FAILURE, activityCategory: action.activityCategory, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.ACTIVITYCATEGORY_SAVE, activityCategory: action.activityCategory, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertActivityCategorySave(action.activityCategory, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.ACTIVITYCATEGORY_SAVE_FAILURE, activityCategory: action.activityCategory, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {ActivityCategoryAction} action
 * @param {ActivityCategory[]} action.activityCategories
 */
export function* saveManyActivityCategories(action: ActivityCategoryAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ ACTIVITYCATEGORY_SAVE_MANY saga', action.activityCategories.map(activityCategory => activityCategory.id).join(','));

        if (!Array.isArray(action.activityCategories)) {
            console.error('saveManyActivityCategories expected an array, received', action.activityCategories);
            throw new Error('saveManyActivityCategories expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveActivityCategory), under these queue keys
		const dispatchedAddedOns: number[] = action.activityCategories.map(activityCategory => activityCategory?.addedOn);
		const revertAll = () => action.activityCategories.map((activityCategory, i) =>
			put(Actions.revertActivityCategorySave(activityCategory, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: ActivityCategory[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.activityCategories.length; i++) {
				const activityCategory = action.activityCategories[i];
				activityCategory.persisted = false;
				ensureOperationId(activityCategory);
				toSave.push(activityCategory);
			}

			const addResult = yield call(idb.add, 'activityCategory', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyActivityCategories: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.ACTIVITYCATEGORY_SAVE_MANY_FAILURE, activityCategories: action.activityCategories, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.ACTIVITYCATEGORY_SAVE_MANY, activityCategories: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.ACTIVITYCATEGORY_SAVE_MANY_FAILURE, activityCategories: action.activityCategories, status: 500, err});

			console.error(`Error saving ${action.activityCategories.length} activityCategories to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {ActivityCategoryAction} action
 */
export function* removeActivityCategory(action: ActivityCategoryAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.activityCategory === undefined || action.activityCategory === null || !(action.activityCategory instanceof ActivityCategory)) {
			console.error('removeActivityCategory expected type ActivityCategory, received', action.activityCategory);
			reportQueued(false);
			throw new Error('removeActivityCategory expected type ActivityCategory');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.activityCategory.status = 'D';
		action.activityCategory.addedOn = newHRTimestamp();
		action.activityCategory.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.activityCategory);
		const addResult = yield call(idb.add, 'activityCategory', action.activityCategory);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeActivityCategory: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.ACTIVITYCATEGORY_REMOVE_FAILURE, activityCategory: action.activityCategory, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ACTIVITYCATEGORY_REMOVE, activityCategory: action.activityCategory});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ACTIVITYCATEGORY_REMOVE_FAILURE, activityCategory: action.activityCategory, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this ActivityCategory.
	*
	* @param {ActivityCategoryAction} action
	*/
export function* restoreActivityCategory(action: ActivityCategoryAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreActivityCategory expected getCurrentUserId() to return a value');
        return;
    }

	if (action.activityCategory === undefined || action.activityCategory === null) {
        console.error('restoreActivityCategory expected a ActivityCategory, received null');
        throw new Error('restoreActivityCategory expected a ActivityCategory, received null');
    }
    else if (!(action.activityCategory instanceof ActivityCategory)) action.activityCategory = new ActivityCategory(action.activityCategory); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.activityCategory.status = 'U';
		action.activityCategory.addedOn = newHRTimestamp();
		action.activityCategory.addedBy = userId;
		action.activityCategory.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.activityCategory);
		const addResult = yield call(idb.add, 'activityCategory', action.activityCategory);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreActivityCategory: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ACTIVITYCATEGORY_RESTORE_FAILURE, activityCategory: action.activityCategory, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ACTIVITYCATEGORY_RESTORE, activityCategory: action.activityCategory});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ACTIVITYCATEGORY_RESTORE_FAILURE, activityCategory: action.activityCategory, status: 500, err});

		console.error('Error restoring from database', err);
	}
}